import React, { createContext, useContext, useEffect, useState } from 'react';

type PaletteTheme = 'emerging' | 'fortune500' | 'authority' | 'maxgrowth' | 'creative' | 'modern';

interface PaletteContextType {
  currentPalette: PaletteTheme;
  setPalette: (palette: PaletteTheme) => void;
  palettes: Record<PaletteTheme, { name: string; description: string; colors: Record<string, string> }>;
}

const PaletteContext = createContext<PaletteContextType | undefined>(undefined);

const palettes = {
  emerging: {
    name: "Emerging Brand",
    description: "Approachable creative consultant",
    colors: {
      '--brand-blue': '225 97% 30%',        /* Original #030e93 */
      '--brand-gold': '42 64% 63%',         /* Original #deb362 */
      '--brand-teal': '177 36% 26%',        /* Original #295f5b */
      '--brand-beige': '34 35% 91%',        /* Original #efe7d9 */
      '--primary': '225 97% 30%',
      '--secondary': '34 35% 91%',
      '--accent': '42 64% 63%',
      '--teal': '177 36% 26%',
      '--muted-foreground': '177 20% 40%',
      '--border': '34 25% 88%',
    }
  },
  fortune500: {
    name: "Fortune 500",
    description: "Strategic innovation authority",
    colors: {
      '--brand-blue': '220 25% 15%',         /* Midnight Blue #1a202c */
      '--brand-gold': '42 71% 42%',          /* Copper Bronze #b7791f */
      '--brand-teal': '213 12% 34%',         /* Sage Gray #4a5568 */
      '--brand-beige': '50 100% 98%',        /* Cream #fffef7 */
      '--primary': '220 25% 15%',
      '--secondary': '50 100% 98%',
      '--accent': '42 71% 42%',
      '--teal': '213 12% 34%',
      '--muted-foreground': '213 12% 45%',
      '--border': '213 12% 88%',
    }
  },
  authority: {
    name: "Executive Authority",
    description: "McKinsey-level institutional trust",
    colors: {
      '--brand-blue': '220 40% 12%',         /* Deep Navy #0a1628 */
      '--brand-gold': '45 100% 42%',         /* Platinum Gold #d4af37 */
      '--brand-teal': '215 20% 25%',         /* Charcoal #2d3748 */
      '--brand-beige': '34 30% 97%',         /* Warm White #faf8f5 */
      '--primary': '220 40% 12%',
      '--secondary': '34 30% 97%',
      '--accent': '45 100% 42%',
      '--teal': '215 20% 25%',
      '--muted-foreground': '215 15% 40%',
      '--border': '215 15% 85%',
    }
  },
  maxgrowth: {
    name: "Maximum Authority & Growth",
    description: "Goldman Sachs meets IDEO",
    colors: {
      '--brand-blue': '215 45% 8%',          /* Deeper Navy #0f1419 */
      '--brand-gold': '4 86% 69%',           /* Strategic Coral #ff6b6b */
      '--brand-teal': '220 9% 46%',          /* Warm Gray #6b7280 */
      '--brand-beige': '210 20% 98%',        /* Clean White */
      '--primary': '215 45% 8%',
      '--secondary': '210 20% 98%',
      '--accent': '4 86% 69%',
      '--teal': '220 9% 46%',
      '--muted-foreground': '220 9% 46%',
      '--border': '220 13% 91%',
    }
  },
  creative: {
    name: "Creative Leadership",
    description: "Premium creative strategist",
    colors: {
      '--brand-blue': '350 50% 25%',         /* Rich Burgundy #722f37 */
      '--brand-gold': '45 85% 75%',          /* Champagne Gold #f7e7ce */
      '--brand-teal': '100 25% 65%',         /* Sage Green #87a96b */
      '--brand-beige': '45 85% 95%',         /* Warm Cream */
      '--primary': '350 50% 25%',
      '--secondary': '45 85% 95%',
      '--accent': '45 85% 75%',
      '--teal': '100 25% 65%',
      '--muted-foreground': '100 15% 45%',
      '--border': '45 25% 88%',
    }
  },
  modern: {
    name: "Modern Strategic Edge",
    description: "Contemporary, less traditional",
    colors: {
      '--brand-blue': '220 25% 15%',         /* Midnight Blue #1a202c */
      '--brand-gold': '45 96% 53%',          /* Amber #fbbf24 */
      '--brand-teal': '215 16% 32%',         /* Charcoal #4b5563 */
      '--brand-beige': '50 100% 98%',        /* Cream #fffef7 */
      '--primary': '220 25% 15%',
      '--secondary': '50 100% 98%',
      '--accent': '45 96% 53%',
      '--teal': '215 16% 32%',
      '--muted-foreground': '215 16% 47%',
      '--border': '215 13% 88%',
    }
  }
};

export const PaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentPalette, setCurrentPalette] = useState<PaletteTheme>('authority');

  const setPalette = (palette: PaletteTheme) => {
    setCurrentPalette(palette);
    
    // Apply colors to CSS custom properties
    const root = document.documentElement;
    const paletteColors = palettes[palette].colors;
    
    Object.entries(paletteColors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  };

  useEffect(() => {
    // Apply initial palette
    setPalette(currentPalette);
  }, []);

  return (
    <PaletteContext.Provider value={{ currentPalette, setPalette, palettes }}>
      {children}
    </PaletteContext.Provider>
  );
};

export const usePalette = () => {
  const context = useContext(PaletteContext);
  if (context === undefined) {
    throw new Error('usePalette must be used within a PaletteProvider');
  }
  return context;
};